"use client";

import { useCallback, useContext, useEffect, useMemo } from "react";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useWilsonActivityStore } from "@/stores/wilsonActivityStore";
import { toggleChat, useIsChatInFront } from "@/stores/chatAppStore";
import { cn } from "@/lib/utils";
import { Button, Keycap, WilsonPresence } from "@/components/desktop/ui";
import { TopBarContext } from "./TopBarContext";
import { LANDED_MS, deriveIslandWilsonView, islandWilsonMotion, type IslandWilsonCopy } from "./islandWilson.utils";
export function useIslandWilson() {
  const t = useClientTranslations("search");
  const ctx = useContext(TopBarContext);
  const runs = useWilsonActivityStore(state => state.runs);
  const landing = useWilsonActivityStore(state => state.landing);
  const clearLanding = useWilsonActivityStore(state => state.clearLanding);
  const openConversation = ctx?.openConversation;
  const copy: IslandWilsonCopy = useMemo(() => ({
    workingOn: question => t("wilsonWorkingOn", {
      question
    }),
    working: t("wilsonWorking")
  }), [t]);
  const view = useMemo(() => deriveIslandWilsonView({
    state: {
      runs,
      landing
    },
    copy
  }), [runs, landing, copy]);
  const openAnswer = useCallback(() => {
    if (view.phase === "idle") return;
    const {
      target
    } = view;
    openConversation?.(target.kind === "thread" ? {
      threadId: target.threadId
    } : {});
    if (view.phase === "landed") clearLanding();
  }, [view, openConversation, clearLanding]);
  return {
    view,
    openAnswer
  };
}
export function IslandWilson() {
  const t = useClientTranslations("search");
  const isReducedMotion = usePrefersReducedMotion();
  const isChatInFront = useIsChatInFront();
  const landing = useWilsonActivityStore(state => state.landing);
  const clearLanding = useWilsonActivityStore(state => state.clearLanding);
  const {
    view,
    openAnswer
  } = useIslandWilson();
  const {
    mood
  } = islandWilsonMotion({
    phase: view.phase,
    isReducedMotion
  });
  useEffect(() => {
    if (!landing) return;
    const timer = setTimeout(clearLanding, LANDED_MS);
    return () => clearTimeout(timer);
  }, [landing, clearLanding]);
  const isIdle = view.phase === "idle";
  const isPressed = isIdle && isChatInFront;
  return <Button variant="control" size="sm" onClick={isIdle ? toggleChat : openAnswer} aria-label={isIdle ? t("wilsonChat") : `${view.line}. ${t("wilsonOpenAnswer")}`} aria-pressed={isIdle ? isChatInFront : undefined} aria-keyshortcuts={isIdle ? "Meta+J" : undefined} data-island-wilson={view.phase} className={cn("group flex-shrink-0 gap-1.5 rounded-full px-3 text-ink", isPressed ? "bg-ink text-surface hover:bg-ink hover:text-surface" : "bg-ink/[0.05]")} data-sentry-element="Button" data-sentry-component="IslandWilson" data-sentry-source-file="IslandWilson.tsx">
            <WilsonPresence mood={mood} className={isPressed ? "text-surface" : undefined} data-sentry-element="WilsonPresence" data-sentry-source-file="IslandWilson.tsx" />
            <span className="font-medium">{t("wilsonChat")}</span>
            {!isPressed && <Keycap className="hidden group-hover:inline">⌘J</Keycap>}
        </Button>;
}